import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  SafeAreaView,
  Image,
  ActivityIndicator,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../screens/ThemeContext';
import { useAuth } from '../private/AuthContext';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { useFocusEffect } from '@react-navigation/native';

const StatusBar = Platform.OS === 'web' ? () => null : require('react-native').StatusBar;

export default function HistoriqueScreen({ navigation, route }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [purchasedDocuments, setPurchasedDocuments] = useState([]);
  // State pour suivre les nouvelles achats et forcer l'actualisation
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Utiliser useFocusEffect pour recharger les données quand l'écran devient actif
  useFocusEffect(
    useCallback(() => {
      if (user) {
        fetchPurchasedDocuments();
      }
    }, [user, refreshTrigger])
  );
  
  // Fonction pour déclencher une actualisation
  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchPurchasedDocuments = async () => {
    try {
      setLoading(true);
      
      // Récupérer les achats de l'utilisateur
      const purchasesQuery = query(
        collection(db, 'purchases'),
        where('userId', '==', user.uid),
        where('status', '==', 'completed'),
        orderBy('purchaseDate', 'desc')
      );
      
      const purchasesSnapshot = await getDocs(purchasesQuery);
      const documentsData = [];

      // Créer un Map pour regrouper les épreuves par concours
      const concoursMap = new Map();
  
      for (const purchaseDoc of purchasesSnapshot.docs) {
        const purchase = purchaseDoc.data();
  
        try {
          if (purchase.documentType === 'concours') {
            // Pour les concours, récupérer le document principal
            const concoursRef = doc(db, 'concours', purchase.documentId);
            const concoursDoc = await getDoc(concoursRef);
            
            if (concoursDoc.exists()) {
              const concoursData = concoursDoc.data();
              
              // Si ce concours n'est pas déjà dans notre Map
              if (!concoursMap.has(purchase.documentId)) {
                concoursMap.set(purchase.documentId, {
                  id: concoursDoc.id,
                  ...concoursData,
                  type: 'concours',
                  purchasedEpreuves: [],
                  purchaseDate: purchase.purchaseDate
                });
              }
              
              // Ajouter l'épreuve achetée
              const concours = concoursMap.get(purchase.documentId);
              // NE PAS stocker purchase.url : cette URL est figée au moment de
              // l'achat et devient obsolète dès que l'admin remplace le PDF.
              // L'URL est résolue en temps réel par PurchaseService.getDocumentUrl.
              concours.purchasedEpreuves.push({
                titre: purchase.titre,
                annee: purchase.annee,
                filiere: purchase.filiere,
                prix: purchase.prix,
                nombrePages: purchase.nombrePages,
                ...(purchase.groupId ? { groupId: purchase.groupId } : {}),
                // Identité stable + id d'achat : résolution exacte et auto-réparation.
                ...(purchase.epreuveId ? { epreuveId: purchase.epreuveId } : {}),
                purchaseId: purchaseDoc.id,
              });
            }
          } else if (purchase.documentType === 'bords') {
            // Pour les bords, récupérer le document principal pour obtenir l'image
            const bordsRef = doc(db, 'bords', purchase.documentId);
            const bordsDoc = await getDoc(bordsRef);
            
            if (bordsDoc.exists()) {
              const bordsData = bordsDoc.data();
              
              documentsData.push({
                id: purchase.documentId,
                titre: purchase.titre,
                prix: purchase.prix,
                nombrePages: purchase.nombrePages,
                url: purchase.url,
                image: bordsData.image, // Récupérer l'URL de l'image du document bord
                description: bordsData.description || '',
                type: 'bords',
                purchaseDate: purchase.purchaseDate
              });
            } else {
              // Si le document n'existe plus, on ajoute quand même l'achat
              documentsData.push({
                id: purchase.documentId,
                titre: purchase.titre,
                prix: purchase.prix,
                nombrePages: purchase.nombrePages,
                url: purchase.url,
                image: null, // Pas d'image disponible
                type: 'bords',
                purchaseDate: purchase.purchaseDate
              });
            }
          }
        } catch (error) {
          console.error('Error fetching document:', error);
        }
      }

      // Ajouter les concours au tableau final
      documentsData.push(...Array.from(concoursMap.values()));
  
      setPurchasedDocuments(documentsData);
      await AsyncStorage.setItem('purchasedDocuments', JSON.stringify(documentsData));
      
      console.log(`Chargement terminé: ${documentsData.length} documents achetés trouvés`);
    } catch (error) {
      console.error('Error fetching purchased documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupDocumentsByType = () => {
    const groups = {
      'concours': {
        title: 'Sujets de Concours',
        icon: 'school',
        data: []
      },
      'bords': {
        title: 'Bords',
        icon: 'book-open-variant',
        data: []
      }
    };
  
    purchasedDocuments.forEach(doc => {
      if (groups[doc.type]) {
        groups[doc.type].data.push(doc);
      }
    });
  
    return Object.fromEntries(
      Object.entries(groups).filter(([_, group]) => group.data.length > 0)
    );
  };

  const documentGroups = groupDocumentsByType();

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleOpenDocument = (document) => {
    // Naviguer vers l'écran de détail avec le callback pour actualiser l'historique
    navigation.navigate('HistoriqueDetailScreen', { 
      document,
      onPurchaseComplete: () => {
        // Déclencher l'actualisation de l'écran d'historique
        console.log("Nouvel achat effectué, actualisation de l'historique");
        triggerRefresh();
      }
    });
  };

  const renderDocumentCard = (document) => (
    <TouchableOpacity
      key={document.id}
      style={[styles.documentCard, { backgroundColor: theme.surface, borderColor: theme.border }]}
      onPress={() => handleOpenDocument(document)}
    >
      <Image
        source={{ 
          uri: document.type === 'concours' 
            ? document.coverImage 
            : document.image || 'https://firebasestorage.googleapis.com/v0/b/myconcours-ec9cb.firebasestorage.app/o/recrutement-job-best-internationnal-etranger.jpg?alt=media&token=42c63c60-28ed-4e47-bf6e-5c6c9353593a' // Utiliser une image par défaut si aucune image n'est disponible
        }}
        style={styles.documentImage}
         
      />
      <View style={styles.documentInfo}>
        <Text style={[styles.documentTitle, { color: theme.text }]}>
          {document.type === 'concours' ? 
            (document.sigle ? `${document.nomComplet} (${document.sigle})` : document.nomComplet) : 
            document.titre}
        </Text>
        {document.type === 'concours' ? (
          <View style={styles.documentStats}>
            <Text style={[styles.statsText, { color: theme.textSecondary }]}>
              {document.purchasedEpreuves.length} épreuve{document.purchasedEpreuves.length > 1 ? 's' : ''} achetée{document.purchasedEpreuves.length > 1 ? 's' : ''}
            </Text>
          </View>
        ) : (
          <Text style={[styles.documentDescription, { color: theme.textSecondary }]} numberOfLines={2}>
            {document.description || 'Aucune description disponible'}
          </Text>
        )}
        <View style={styles.documentMeta}>
          <View style={[styles.badge, { backgroundColor: theme.primary + '20' }]}>
            <MaterialCommunityIcons
              name={document.type === 'concours' ? 'school' : 'book-open-variant'}
              size={14}
              color={theme.primary}
            />
            <Text style={[styles.badgeText, { color: theme.primary }]}>
              {document.type === 'concours' ? 'Concours' : 'Bord'}
            </Text>
          </View>
          <Text style={[styles.purchaseDate, { color: theme.textSecondary }]}>
            Dernier achat le {formatDate(document.purchaseDate)}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  const filterDocuments = () => {
    if (!searchQuery.trim()) return purchasedDocuments;
    
    const query = searchQuery.toLowerCase();
    return purchasedDocuments.filter(doc => {
      const searchableText = `
        ${doc.sigle || ''}
        ${doc.titre || ''}
        ${doc.nomComplet || ''}
        ${doc.description || ''}
      `.toLowerCase();
      
      return searchableText.includes(query);
    });
  };

  const filteredDocuments = filterDocuments();

  // Render empty state when no purchases exist
  const renderEmptyState = () => (
    <View style={styles.emptyStateContainer}>
      <MaterialCommunityIcons 
        name="file-document-outline" 
        size={80} 
        color={theme.textSecondary} 
      />
      <Text style={[styles.emptyStateTitle, { color: theme.text }]}>
        Aucun historique trouvé
      </Text>
      <Text style={[styles.emptyStateMessage, { color: theme.textSecondary }]}>
        Vous n'avez pas encore acheté de documents
      </Text>
      
    </View>
  );

  // ===== Écran pour utilisateur NON connecté =====
  if (!user) {
    return (
      <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.primary }]}>
        <StatusBar barStyle="light-content" backgroundColor={theme.primary} />
        <ScrollView style={[styles.container, { backgroundColor: theme.background }]}>
          <LinearGradient
            colors={[theme.primary, theme.primaryDark]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.header}
          >
            <Text style={styles.headerTitle}>Mes Documents</Text>
          </LinearGradient>

          <View style={styles.guestContainer}>
            <MaterialCommunityIcons name="file-lock" size={80} color={theme.textSecondary} />
            <Text style={[styles.guestTitle, { color: theme.text }]}>
              Vous n'êtes pas connecté
            </Text>
            <Text style={[styles.guestMessage, { color: theme.textSecondary }]}>
              Connectez-vous pour retrouver vos documents achetés et votre historique.
            </Text>
            <View style={styles.guestButtonsRow}>
              <TouchableOpacity
                style={[styles.guestLoginBtn, { backgroundColor: theme.primary }]}
                onPress={() => navigation.navigate('LoginScreen')}
              >
                <MaterialCommunityIcons name="login" size={20} color="#fff" />
                <Text style={styles.guestLoginBtnText}>Connexion</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.guestRegisterBtn, { borderColor: theme.primary }]}
                onPress={() => navigation.navigate('RegisterScreen')}
              >
                <MaterialCommunityIcons name="account-plus" size={20} color={theme.primary} />
                <Text style={[styles.guestRegisterBtnText, { color: theme.primary }]}>Inscription</Text>
              </TouchableOpacity>
            </View>
          </View>
          <View style={styles.bottomSpace} />
        </ScrollView>
      </SafeAreaView>
    );
  }

  // ===== Écran pour utilisateur CONNECTÉ =====
  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.primary }]}>
      <StatusBar barStyle="light-content" backgroundColor={theme.primary} />
      <ScrollView style={[styles.container, { backgroundColor: theme.background }]}>
        {/* Header */}
        <LinearGradient
          colors={[theme.primary, theme.primaryDark]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.header}
        >
          <Text style={styles.headerTitle}>Mes Documents</Text>
        </LinearGradient>

        {/* Search Section */}
        <View style={[styles.searchSection, { backgroundColor: theme.surface }]}>
          <View style={[styles.searchBar, { backgroundColor: theme.background }]}>
            <MaterialCommunityIcons name="magnify" size={24} color={theme.textSecondary} />
            <TextInput
              style={[styles.searchInput, { color: theme.text }]}
              placeholder="Rechercher un document..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor={theme.textSecondary}
            />
          </View>
        </View>

        {/* Document List or Empty State */}
        {loading ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color={theme.primary} />
          </View>
        ) : purchasedDocuments.length === 0 ? (
          renderEmptyState()
        ) : (
          <View style={styles.documentsContainer}>
            {Object.entries(documentGroups).map(([type, group]) => (
              group.data.length > 0 && (
                <View key={type} style={styles.documentGroup}>
                  <View style={styles.groupHeader}>
                    <MaterialCommunityIcons name={group.icon} size={24} color={theme.primary} />
                    <Text style={[styles.groupTitle, { color: theme.text }]}>{group.title}</Text>
                    <View style={styles.documentCount}>
                      <Text style={[styles.countText, { color: theme.textSecondary }]}>
                        {group.data.length}
                      </Text>
                    </View>
                  </View>
                  {group.data
                    .filter(doc => {
                      if (!searchQuery) return true;
                      const search = searchQuery.toLowerCase();
                      return (
                        (doc.sigle?.toLowerCase() || '').includes(search) ||
                        (doc.titre?.toLowerCase() || '').includes(search) ||
                        (doc.description?.toLowerCase() || '').includes(search)
                      );
                    })
                    .map(doc => renderDocumentCard(doc))}
                </View>
              )
            ))}
          </View>
        )}
        <View style={styles.bottomSpace} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  header: {
    paddingTop: 20,
    paddingBottom: 30,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
  },
  searchSection: {
    padding: 20,
    margin: 20,
    marginTop: -20,
    borderRadius: 20,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 15,
    elevation: 5,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    paddingHorizontal: 15,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    marginLeft: 10,
    fontSize: 16,
  },
  // Empty state styles
  emptyStateContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    marginTop: 60,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 10,
    textAlign: 'center',
  },
  emptyStateMessage: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  emptyStateButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    marginTop: 16,
  },
  emptyStateButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  // Existing styles
  documentsContainer: {
    padding: 20,
  },
  documentGroup: {
    marginBottom: 30,
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  groupTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginLeft: 10,
    flex: 1,
  },
  documentCount: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  countText: {
    fontSize: 14,
    fontWeight: '600',
  },
  documentCard: {
    borderRadius: 16,
    marginBottom: 15,
    borderWidth: 1,
    overflow: 'hidden',
  },
  documentImage: {
    width: '100%',
    height: 150,
    backgroundColor: '#f0f0f0',
  },
  documentInfo: {
    padding: 15,
  },
  documentTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  documentStats: {
    marginBottom: 12,
  },
  statsText: {
    fontSize: 14,
  },
  documentDescription: {
    fontSize: 14,
    marginBottom: 12,
    lineHeight: 20,
  },
  documentMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  purchaseDate: {
    fontSize: 12,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  bottomSpace: {
    height: 100,
  },
  guestContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    marginTop: 40,
  },
  guestTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 10,
    textAlign: 'center',
  },
  guestMessage: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 30,
  },
  guestButtonsRow: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  guestLoginBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
    gap: 8,
    borderRadius: 15,
  },
  guestLoginBtnText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 5,
  },
  guestRegisterBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
    gap: 8,
    borderWidth: 1,
    borderRadius: 15,
  },
  guestRegisterBtnText: {
    fontWeight: '600',
    marginLeft: 5,
  },
});